import { useState, useEffect, useCallback } from 'react';
import { creditsApi, formatCurrencyWhole } from '../utils/api';
import { useSettings } from '../context/SettingsContext';
import { vatMultiplier } from '../utils/money';
import InlineLoadError from './InlineLoadError';
import { CREDIT_STATUS } from '../utils/creditsShared';

// The always-visible picture, independent of which CreditsViewTabs tab is
// active: how much is still open, how much came back, how much was
// cancelled, and how many credits are open. Deliberately NOT scoped to
// either tab's locked status — it fetches the whole period itself (own
// request, own loading state) so switching tabs never moves these numbers,
// which is what makes their position (right after the date picker, above
// the tabs) read as "the period's total picture" rather than "a filtered
// result that happens to sit near the tabs." Rendered identically by
// CreditsOpenPage and CreditsHistoryPage.
//
// `refreshKey` — bump it to force a refetch. Because this component owns its
// request, a page mutation (receive / cancel / bulk receive) updates the
// TABLE without touching these numbers; the pages therefore route every
// post-mutation refresh through a `reloadAll()` that both re-runs their own
// load and increments this key. Without it the cards keep reporting
// pre-mutation totals until the date changes or the page reloads.
export default function CreditsMetricsRow({ dateFrom, dateTo, nameFilter, refreshKey = 0 }) {
  const { vatRate } = useSettings();
  const vatMult = vatMultiplier(vatRate);
  const [credits, setCredits] = useState([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  // The period `credits` was fetched for — see SuppliersPage. setLoading runs
  // inside the effect, one frame after the new dates have already painted.
  const [loadedKey, setLoadedKey] = useState(null);
  // Bumped by the retry button; joins refreshKey as a fetch trigger.
  const [attempt, setAttempt] = useState(0);

  const key = `${dateFrom || ''}|${dateTo || ''}`;

  useEffect(() => {
    let cancelled = false;
    setError(false);
    setLoading(true);
    creditsApi.getAll({ date_from: dateFrom || undefined, date_to: dateTo || undefined })
      .then(r => { if (!cancelled) { setCredits(r.data); setLoadedKey(`${dateFrom || ''}|${dateTo || ''}`); } })
      // A failed fetch must never render as an empty result — "—" here is
      // indistinguishable from "this period genuinely has no credits", and
      // these are money figures. Surface it instead. (Also stops the
      // rejection from surfacing as an unhandled promise error.)
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dateFrom, dateTo, refreshKey, attempt]);

  const retry = useCallback(() => setAttempt(a => a + 1), []);

  if (error) {
    return (
      <div className="stats-row" style={{ justifyContent: 'center' }}>
        <InlineLoadError message="לא ניתן לטעון את סיכום הזיכויים" onRetry={retry} />
      </div>
    );
  }

  const inPeriod = nameFilter
    ? credits.filter(c => c.supplier_name.includes(nameFilter))
    : credits;
  // '—' means "there is nothing here to report"; '0' means "your filter
  // produced zero". Those are different answers and the user asked a
  // different question in each case, so a SEARCH that matches nothing shows
  // zeros — dashes there read as "the cards went blank / stopped working"
  // rather than "no credits for this supplier".
  const showDash = !nameFilter && inPeriod.length === 0;

  // While a fetch is in flight `credits` still holds the PREVIOUS period's
  // rows. Showing them would print the old period's money under the new
  // period's date picker — the failure mode that got reported on /suppliers,
  // where a month switch looked like the filter had silently done nothing.
  // A skeleton is the answer to the dilemma that kept this ungated: it is
  // neither a stale number nor a flash of ₪0.00, and it holds the card's
  // height so the tabs and table below do not jump.
  const skel = <span className="stat-skeleton" aria-hidden="true" />;
  const pending = loading || loadedKey !== key;
  // Both branches render a non-breaking space rather than nothing, so the
  // third line keeps its height whether or not it carries a figure.
  const sub = (text) => (pending || showDash ? ' ' : text);

  const open      = inPeriod.filter(c => c.status === CREDIT_STATUS.OPEN);
  const received  = inPeriod.filter(c => c.status === CREDIT_STATUS.RECEIVED);
  const cancelled = inPeriod.filter(c => c.status === CREDIT_STATUS.CANCELLED);
  const sum = (rows) => rows.reduce((s, c) => s + (c.credit_total || 0), 0);
  const openNet      = sum(open);
  const receivedNet  = sum(received);
  const cancelledNet = sum(cancelled);

  return (
    // `--quad`: four cards, so they lay out 2x2 on mobile instead of a
    // horizontal strip the user has to swipe to see half of. See index.css.
    <div className="stats-row stats-row--quad" aria-busy={pending || undefined}>
      <div className="stat-card">
        <div className="stat-value">{pending ? skel : showDash ? '—' : open.length}</div>
        <div className="stat-label">כמות זיכויים פתוחים</div>
      </div>
      <div className="stat-card">
        <div className="stat-value" style={{ color: showDash ? 'var(--ink-subtle)' : 'var(--warning-ink)' }}>
          {pending ? skel : showDash ? '—' : formatCurrencyWhole(openNet)}
        </div>
        <div className="stat-label">זיכויים פתוחים · ללא מע״מ</div>
        <div className="stat-sub">
          {sub(`כולל מע״מ ${formatCurrencyWhole(openNet * vatMult)}`)}
        </div>
      </div>
      <div className="stat-card">
        <div className="stat-value" style={{ color: showDash ? 'var(--ink-subtle)' : 'var(--success-ink)' }}>
          {pending ? skel : showDash ? '—' : formatCurrencyWhole(receivedNet)}
        </div>
        <div className="stat-label">זיכויים שהתקבלו · ללא מע״מ</div>
        <div className="stat-sub">
          {sub(`כולל מע״מ ${formatCurrencyWhole(receivedNet * vatMult)}`)}
        </div>
      </div>
      {/* Muted, not amber/green — cancelled money is neither owed nor a
          reward, and shouldn't compete visually with the two that are. */}
      <div className="stat-card" style={{ opacity: showDash ? 1 : 0.8 }}>
        <div className="stat-value" style={{ color: 'var(--ink-muted)' }}>
          {pending ? skel : showDash ? '—' : formatCurrencyWhole(cancelledNet)}
        </div>
        <div className="stat-label" style={{ color: 'var(--ink-muted)' }}>זיכויים שבוטלו · ללא מע״מ</div>
      </div>
    </div>
  );
}
